// Thin API client for /api/gym/*. The shared fetch/error-handling logic
// lives in platform/apiClient/apiRequest.ts, reused by every module.

import { request, ApiError } from "../../platform/apiClient/apiRequest";
import type {
  Booking,
  BookingWithAttendee,
  DayOverride,
  GymProfile,
  GymSlot,
  ProfileInput,
  SlotOverride,
} from "../types/gym.types";

export { ApiError };

export const gymClient = {
  health: () => request<{ status: string; timestamp: string }>("/gym/health"),

  /**
   * Returns null when the profile isn't completed yet (the API answers 404),
   * so callers can branch on "needs setup" without catching an error.
   */
  getProfile: async (): Promise<GymProfile | null> => {
    try {
      return await request<GymProfile>("/gym/profile");
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },

  saveProfile: (input: ProfileInput) =>
    request<GymProfile>("/gym/profile", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  getSlots: (from: string, to: string) =>
    request<GymSlot[]>(`/gym/slots?from=${from}&to=${to}`),

  getBookings: () => request<Booking[]>("/gym/bookings"),

  createBooking: (slotId: string) =>
    request<Booking>("/gym/bookings", {
      method: "POST",
      body: JSON.stringify({ slotId }),
    }),

  cancelBooking: (id: string) =>
    request<Booking>(`/gym/bookings/${encodeURIComponent(id)}`, { method: "DELETE" }),

  getCoachSchedule: (date: string) =>
    request<BookingWithAttendee[]>(`/gym/coach/bookings?date=${date}`),

  checkInBooking: (id: string) =>
    request<Booking>(`/gym/bookings/${encodeURIComponent(id)}/check-in`, { method: "POST" }),

  markNoShow: (id: string) =>
    request<Booking>(`/gym/bookings/${encodeURIComponent(id)}/no-show`, { method: "POST" }),

  // ---- Admin ----

  getAdminEmployees: () => request<GymProfile[]>("/gym/admin/employees"),

  updateAdminEmployee: (userId: string, patch: { isBlocked?: boolean; noShowCount?: number }) =>
    request<GymProfile>(`/gym/admin/employees/${encodeURIComponent(userId)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  getAdminBookings: (date: string) =>
    request<BookingWithAttendee[]>(`/gym/admin/bookings?date=${date}`),

  adminCancelBooking: (id: string) =>
    request<Booking>(`/gym/admin/bookings/${encodeURIComponent(id)}`, { method: "DELETE" }),

  getDayOverrides: (from: string, to: string) =>
    request<DayOverride[]>(`/gym/admin/day-overrides?from=${from}&to=${to}`),

  setDayOverride: (date: string, patch: { isClosed?: boolean; capacityOverride?: number | null; note?: string }) =>
    request<DayOverride>(`/gym/admin/day-overrides/${date}`, {
      method: "PUT",
      body: JSON.stringify(patch),
    }),

  clearDayOverride: (date: string) =>
    request<void>(`/gym/admin/day-overrides/${date}`, { method: "DELETE" }),

  getSlotOverrides: (from: string, to: string) =>
    request<SlotOverride[]>(`/gym/admin/slot-overrides?from=${from}&to=${to}`),

  setSlotOverride: (
    date: string,
    startTime: string,
    patch: { isClosed?: boolean; capacityOverride?: number | null; note?: string },
  ) =>
    request<SlotOverride>(`/gym/admin/slot-overrides/${date}/${encodeURIComponent(startTime)}`, {
      method: "PUT",
      body: JSON.stringify(patch),
    }),

  clearSlotOverride: (date: string, startTime: string) =>
    request<void>(`/gym/admin/slot-overrides/${date}/${encodeURIComponent(startTime)}`, { method: "DELETE" }),
};
